/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import { NewButton } from '../../../../global';
import type { Tag as TagType, TagsGet } from '@whizz/api-model';
import { useModal } from '../../../../hooks/useModal';
import { useQuery } from 'react-query';
import { QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import TagsBlock from './modules/TagsBlock';
import type { ModalProps as DeleteTagModalProps } from './modules/modal/DeleteTagModal';
import { AddEditTagModal, DeleteTagModal } from './modules';
import type { ModalProps as AddEditTagModalProps } from './modules/modal/AddEditTagModal';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';

const Tags = (): JSX.Element => {
  const { translate } = useI18n();

  const [ deleteModal, toggleDeleteModal, setDeleteModalProps ] = useModal((props: DeleteTagModalProps) => (
    <DeleteTagModal {...props} />
  ));

  const [ addEditModal, toggleAddEditModal, setAddEditModalProps ] = useModal((props: AddEditTagModalProps) => (
    <AddEditTagModal {...props} />
  ));

  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  const tagsQuery = useQuery<TagsGet>(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
    () => repository.TagsRepository.getTags(),
    {
      enabled: true,
      placeholderData: [],
    },
  );

  const handleAdd = () => {
    setAddEditModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.ADD_TITLE'),
      data: null,
      tagCount: tagsQuery.data!.length,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    toggleAddEditModal(true);
  };

  const handleEdit = (data: TagType) => {
    setAddEditModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.EDIT_TITLE'),
      data,
      tagCount: tagsQuery.data!.length,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    toggleAddEditModal(true);
  };

  const handleDelete = (data: TagType) => {
    setDeleteModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.TAGS.ADD_EDIT.DELETE_TITLE'),
      data,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    toggleDeleteModal(true);
  };

  return (
    <RosterManagementLayout>
      {successErrorModal}
      {addEditModal}
      {deleteModal}
      <div className={'space-y-6'}>
        <TagsBlock
          tags={tagsQuery.data!}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          isLoading={!tagsQuery.isFetched || tagsQuery.isFetching}
        />
        <NewButton
          onClick={handleAdd}
          asLink
          align={'start'}
          color={'primary'}
          data-cy={'rm-add-tag'}
        >
          {translate('pages.ROSTER_MANAGEMENT.TAGS.BUTTONS.ADD')}
        </NewButton>
      </div>
    </RosterManagementLayout>
  );
};

export default Tags;
